/**
 * Multi-Currency Real-Time P&L (Patent Sub-system Y)
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Loader2, FlaskConical, Globe, TrendingUp } from 'lucide-react';
import DemoDataBanner from '../components/DemoDataBanner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CURRENCIES = ['USD', 'EUR', 'GBP', 'INR', 'JPY', 'AUD', 'CAD'];
const SYMBOL = { USD: '$', EUR: '€', GBP: '£', INR: '₹', JPY: '¥', AUD: 'A$', CAD: 'C$' };
const fmt = (n, cur) => `${SYMBOL[cur] || ''}${(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

const MultiCurrency = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reporting, setReporting] = useState('USD');

  useEffect(() => {
    setLoading(true);
    axios.get(`${API_URL}/multi-currency/portfolio`, { params: { reporting } })
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, [reporting]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="multi-currency-page">
      <Helmet><title>Multi-Currency P&amp;L | CARTIEAI</title></Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <DemoDataBanner />
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-cyan-300 border border-emerald-500/30 rounded-md px-2 py-1 mb-5" data-testid="mc-patent-badge">
            <FlaskConical className="w-3 h-3" /> Proprietary engine
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.05]">
            Multi-Currency P&amp;L
          </h1>
          <p className="mt-4 text-base sm:text-lg text-slate-300 leading-relaxed">
            Existing FinOps tools assume USD everywhere. <span className="text-cyan-300">Global SaaS sells in EUR / GBP / INR / JPY / AUD</span>, pays cloud bills in USD, and reports in a 6th currency to investors.
          </p>
          <p className="mt-3 text-sm text-slate-500">
            We isolate <span className="text-cyan-300">FX exposure</span> from operational variance — so you know which margin moves are real and which are just exchange-rate noise.
          </p>
        </div>

        <div className="mt-10 flex items-center gap-3" data-testid="mc-currency-picker">
          <span className="text-xs uppercase tracking-widest text-slate-500"><Globe className="inline w-4 h-4 mr-1" /> Report in:</span>
          {CURRENCIES.map((c) => (
            <button
              key={c}
              onClick={() => setReporting(c)}
              data-testid={`mc-cur-${c}`}
              className={`text-xs font-mono px-3 py-1.5 rounded-full border transition-colors ${reporting === c ? 'bg-emerald-500/20 border-emerald-500/50 text-cyan-200' : 'border-white/10 text-slate-300 hover:border-emerald-500/30 hover:text-cyan-300'}`}
            >
              {c}
            </button>
          ))}
        </div>

        {loading && <Loader2 className="w-8 h-8 animate-spin mx-auto my-12 text-slate-500" />}

        {data && !loading && (
          <>
            <div className={`mt-8 grid grid-cols-1 ${reporting === 'USD' ? 'sm:grid-cols-3' : 'sm:grid-cols-4'} gap-3`} data-testid="mc-summary">
              <Card className="bg-slate-900/60 border border-white/10 shadow-sm border-emerald-500/30"><CardContent className="p-4">
                <div className="text-[10px] uppercase tracking-widest text-emerald-300">Total MRR</div>
                <div className="text-2xl font-bold text-white mt-1" data-testid="mc-total-mrr">{fmt(data.total_mrr_reporting, reporting)}</div>
                <div className="text-[10px] text-slate-500">in {reporting}</div>
              </CardContent></Card>
              <Card className="bg-slate-900/60 border border-white/10 shadow-sm border-rose-500/30"><CardContent className="p-4">
                <div className="text-[10px] uppercase tracking-widest text-rose-300">Total cloud cost</div>
                <div className="text-2xl font-bold text-white mt-1">{fmt(data.total_cost_reporting, reporting)}</div>
                <div className="text-[10px] text-slate-500">in {reporting}</div>
              </CardContent></Card>
              <Card className="bg-slate-900/60 border border-white/10 shadow-sm border-emerald-500/30"><CardContent className="p-4">
                <div className="text-[10px] uppercase tracking-widest text-emerald-300">Total margin</div>
                <div className="text-2xl font-bold text-white mt-1" data-testid="mc-total-margin">{fmt(data.total_margin_reporting, reporting)}</div>
                <div className="text-[10px] text-slate-500">{data.blended_margin_pct}% blended</div>
              </CardContent></Card>
              {reporting !== 'USD' && (
                <Card className="bg-slate-900/60 border border-white/10 shadow-sm border-amber-500/30"><CardContent className="p-4">
                  <div className="text-[10px] uppercase tracking-widest text-amber-300">FX rate vs USD</div>
                  <div className="text-2xl font-bold text-white mt-1">{data.fx_rate_vs_usd}</div>
                  <div className="text-[10px] text-slate-500">{data.is_live_fx ? 'live ECB-aligned' : 'seeded fallback'}</div>
                </CardContent></Card>
              )}
            </div>

            <Card className="mt-8 bg-slate-900/60 border border-white/10 shadow-sm border-white/10">
              <CardHeader>
                <CardTitle className="text-white text-lg flex items-center gap-2"><TrendingUp className="w-5 h-5 text-cyan-300" /> Customer P&amp;L · {data.customer_count} customers</CardTitle>
                <CardDescription>FX exposure column = how much each margin swings between the 30-day FX min and max — your real currency risk.</CardDescription>
              </CardHeader>
              <CardContent>
                <table className="w-full text-sm" data-testid="mc-table">
                  <thead className="text-[10px] uppercase tracking-widest text-slate-500">
                    <tr className="border-b border-white/10">
                      <th className="text-left p-2">Customer</th>
                      <th className="text-left p-2">Country</th>
                      <th className="text-right p-2">MRR (local)</th>
                      <th className="text-right p-2">MRR ({reporting})</th>
                      <th className="text-right p-2">Cost ({reporting})</th>
                      <th className="text-right p-2">Margin</th>
                      <th className="text-right p-2">%</th>
                      <th className="text-right p-2">FX exposure (30d)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.customers.map((c, i) => (
                      <tr key={i} className="border-b border-white/5 hover:bg-slate-950/60">
                        <td className="p-2 text-xs text-white">{c.name}</td>
                        <td className="p-2 text-xs text-slate-300">{c.country}</td>
                        <td className="p-2 text-xs text-right text-slate-200 font-mono">{SYMBOL[c.currency] || ''}{c.mrr_local.toLocaleString()}</td>
                        <td className="p-2 text-xs text-right text-emerald-300 font-mono">{fmt(c.mrr_reporting, reporting)}</td>
                        <td className="p-2 text-xs text-right text-rose-300 font-mono">{fmt(c.cost_reporting, reporting)}</td>
                        <td className={`p-2 text-xs text-right font-mono font-semibold ${c.margin_reporting > 0 ? 'text-cyan-300' : 'text-red-400'}`}>{fmt(c.margin_reporting, reporting)}</td>
                        <td className={`p-2 text-xs text-right font-mono ${c.margin_pct > 0 ? 'text-cyan-300' : 'text-red-400'}`}>{c.margin_pct}%</td>
                        <td className="p-2 text-xs text-right text-amber-300 font-mono">±{fmt(c.fx_exposure_range, reporting)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default MultiCurrency;
